import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Container,
  Avatar,
  Chip
} from '@mui/material';
import {
  Star,
  Assessment,
  TrendingUp,
  VerifiedUser,
  Home,
  Speed
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import StarRating from '../components/Gamification/StarRating';
import AchievementBadge, { ACHIEVEMENTS } from '../components/Gamification/AchievementBadge';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Assessment />,
      title: 'Smart Assessment',
      description: 'Progressive questionnaire that adapts to your property type and compliance level',
      color: '#1976d2'
    },
    {
      icon: <Star />,
      title: 'Star Rating System',
      description: 'Earn stars as you improve your compliance score from Bronze to Platinum',
      color: '#f57c00'
    },
    {
      icon: <TrendingUp />,
      title: 'Level Progression',
      description: 'Unlock new assessment categories as you complete previous levels',
      color: '#388e3c'
    },
    {
      icon: <VerifiedUser />,
      title: 'Official Verification',
      description: 'Get your score verified through document review or property inspection',
      color: '#7b1fa2'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <motion.div variants={itemVariants}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 3,
                bgcolor: 'secondary.main',
                boxShadow: '0 8px 32px rgba(245, 124, 0, 0.3)'
              }}
            >
              <Home sx={{ fontSize: 50 }} />
            </Avatar>

            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 'bold',
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2, #f57c00)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Property Compliance Health Check
            </Typography>

            <Typography variant="h5" color="textSecondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Discover your property's compliance score through our engaging, gamified assessment platform
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 4 }}>
              <Chip icon={<Speed />} label="Quick Start" color="primary" />
              <Chip icon={<Star />} label="Earn Rewards" color="secondary" />
              <Chip icon={<VerifiedUser />} label="Get Verified" color="success" />
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/assessment')}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.2rem',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(25, 118, 210, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Start Your Assessment
            </Button>
          </Box>
        </motion.div>

        {/* Features Grid */}
        <motion.div variants={itemVariants}>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}>
            Why Choose Our Platform?
          </Typography>
        </motion.div>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div variants={itemVariants}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: feature.color, mr: 2 }}>
                        {feature.icon}
                      </Avatar>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="textSecondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Star Rating Demo */}
        <motion.div variants={itemVariants}>
          <Card sx={{ mb: 6, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold' }}>
                Compliance Rating System
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                {/* Zero Star - Critical/Urgent */}
                <Box sx={{ textAlign: 'center' }}>
                  <StarRating rating={0} size="large" />
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontWeight: 'bold', color: '#ffcdd2' }}>
                    Urgent
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>
                    Non-Habitable
                  </Typography>
                </Box>

                {/* Regular Star Ratings */}
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Box key={rating} sx={{ textAlign: 'center' }}>
                    <StarRating rating={rating} size="large" />
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      {rating === 1 && 'Basic'}
                      {rating === 2 && 'Bronze'}
                      {rating === 3 && 'Silver'}
                      {rating === 4 && 'Gold'}
                      {rating === 5 && 'Platinum'}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Rating Scale Description */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
                  From urgent tenancy compliance issues to outstanding rental property standards
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Achievement Showcase */}
        <motion.div variants={itemVariants}>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold' }}>
            Unlock Achievements
          </Typography>
          <Grid container spacing={2} sx={{ mb: 6 }}>
            {ACHIEVEMENTS.slice(0, 4).map((achievement) => (
              <Grid item xs={6} md={3} key={achievement.id}>
                <AchievementBadge achievement={achievement} size="small" showProgress={false} />
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Call to Action */}
        <motion.div variants={itemVariants}>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
              Join thousands of property owners improving their compliance scores
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/assessment')}
              sx={{
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                borderRadius: 3,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Begin Assessment
            </Button>
          </Box>
        </motion.div>
      </Container>
    </motion.div>
  );
};

export default HomePage;